import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract, WorkPackage } from '@/api/generated/models';
import { useSearchActivities } from '@/api/generated/activities/activities';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { useListAllWorkPackages } from '@/api/generated/work-structure/work-structure';
import {
  CardSection,
  ListPagination,
  ListTotalCard,
  ListTotalRow,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  TABLE_WEEK_PRIMARY_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { activityLockState } from '@/features/activities/activityDisplay';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';
import { formatAmount } from '@/utils/format';
import { usePageSize } from '@/utils/usePageSize';
import { useSavedMessage } from '@/utils/useSavedMessage';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const cellStyle = { color: 'var(--color-text-secondary)' };

const HeaderIntro = () => {
  const { t } = useTranslation();
  return <PageHeading title={t('activities.title')} subtitle={t('activities.subtitle')} />;
};

export const ActivitiesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useUrlTextParam('search');
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [objectFilter, setObjectFilter] = useUrlNullableParam('objectId');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, objectFilter, counterpartyFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao (npr. netom kreirana aktivnost mora
  // odmah biti vidljiva, ne tek za 5 min).
  const { data, isLoading } = useSearchActivities(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      objectId: objectFilter ?? undefined,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  // Traka UKUPNO = zbroj TRENUTNE stranice (ne cijelog filtriranog skupa).
  const totalAmount = pageItems.reduce((sum, activity) => sum + (activity.totalAmount ?? 0), 0);
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Aktivnost nema vlastiti status — "U izvještaju" = uključena u tjedni izvještaj (vidi activityLockState).
  const statusOptions = [
    { value: 'ALL', label: t('common.filters.allStatuses') },
    { value: 'RECORDED', label: t('activities.status.recorded') },
    { value: 'IN_REPORT', label: t('activities.status.inReport') }
  ];

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  // Puni cross-referencirani popisi samo za razrješavanje naziva po retku —
  // pretraga po njima ide na backend preko /search (JOIN na contracts/work_packages/teams).
  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contractMap = useMemo(() => {
    const map = new Map<string, Contract>();
    contracts.forEach((c) => c.id && map.set(c.id, c));
    return map;
  }, [contracts]);

  const { data: workPackagesData } = useListAllWorkPackages();
  const workPackages: WorkPackage[] = workPackagesData ?? [];
  const workPackageMap = useMemo(() => {
    const map = new Map<string, WorkPackage>();
    workPackages.forEach((wp) => wp.id && map.set(wp.id, wp));
    return map;
  }, [workPackages]);

  const contractLabel = (contractId?: string) => {
    const c = contractId ? contractMap.get(contractId) : undefined;
    return c?.contractNumber ?? '—';
  };
  const workPackageLabel = (workPackageId?: string) => {
    if (!workPackageId) return t('activities.directFromContract');
    return workPackageMap.get(workPackageId)?.name ?? '—';
  };

  const paginationLabel = t('activities.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const columns: [string, number, ('right' | 'center' | undefined)?][] = [
    ['activities.colWeek', TABLE_WEEK_PRIMARY_COL_WIDTH],
    ['activities.colContractObjectClient', 260],
    ['activities.colAmount', 110, 'right'],
    ['activities.colHours', 70, 'right'],
    ['activities.colWorkPackage', 160],
    ['activities.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
  ];
  const tableMinWidthPx = tableMinWidth(
    [...columns.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
    20
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Group justify="space-between" wrap="nowrap" align="flex-start" visibleFrom="sm">
          <HeaderIntro />
          <Button onClick={() => navigate('/activities/new')}>+ {t('activities.create')}</Button>
        </Group>
        <Stack gap={12} hiddenFrom="sm">
          <HeaderIntro />
          <MobileFab onClick={() => navigate('/activities/new')}>
            + {t('activities.create')}
          </MobileFab>
        </Stack>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('activities.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('activities.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              },
              {
                key: 'object',
                label: t('activities.filterObject'),
                options: objectOptions,
                value: objectFilter,
                onChange: setObjectFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={280}>
            <SearchField
              hideLabel
              placeholder={t('activities.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('activities.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('activities.filterObject')}
              data={objectOptions}
              value={objectFilter}
              onChange={setObjectFilter}
              clearable
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('activities.empty')}
                </Text>
              </Center>
            )}
            {pageItems.length > 0 && (
              <>
                <Box style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidthPx }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {columns.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{
                            color: 'var(--color-text-muted)',
                            letterSpacing: 0.4,
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('activities.colAction')}
                      </Text>
                    </div>
                    {pageItems.map((activity, index) => {
                      const status = activityLockState(activity.weekReportId);
                      return (
                        <div
                          key={activity.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text
                            fz={13.5}
                            fw={600}
                            w={TABLE_WEEK_PRIMARY_COL_WIDTH}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {formatWeekLabel(activity.weekNumber, activity.reportYear)}
                          </Text>
                          <Stack
                            gap={1}
                            w={260}
                            style={{ cursor: activity.contractId ? 'pointer' : undefined }}
                            role={activity.contractId ? 'button' : undefined}
                            tabIndex={activity.contractId ? 0 : undefined}
                            onClick={() =>
                              activity.contractId && navigate(`/contracts/${activity.contractId}`)
                            }
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                activity.contractId
                              )
                                navigate(`/contracts/${activity.contractId}`);
                            }}
                          >
                            <Text
                              fz={13.5}
                              fw={600}
                              truncate
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {contractLabel(activity.contractId)}
                            </Text>
                            <Text fz={11.5} truncate style={{ color: 'var(--color-text-muted)' }}>
                              {[activity.objectName, activity.clientOrgName]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </Text>
                          </Stack>
                          <Text fz={13} w={110} ta="right" style={cellStyle}>
                            {formatAmount(activity.totalAmount)}
                          </Text>
                          <Text fz={13} w={70} ta="right" style={cellStyle}>
                            {activity.totalHours ?? 0}
                          </Text>
                          <Text
                            fz={13}
                            w={160}
                            style={{
                              ...cellStyle,
                              cursor: activity.workPackageId ? 'pointer' : undefined
                            }}
                            truncate
                            role={activity.workPackageId ? 'button' : undefined}
                            tabIndex={activity.workPackageId ? 0 : undefined}
                            onClick={() =>
                              activity.workPackageId &&
                              navigate(`/work-packages/${activity.workPackageId}`)
                            }
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                activity.workPackageId
                              )
                                navigate(`/work-packages/${activity.workPackageId}`);
                            }}
                          >
                            {workPackageLabel(activity.workPackageId)}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} />
                          </div>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_ACTION_COL_WIDTH }}>
                            <RowActionsMenu
                              detailsHref={`/activities/${activity.id}`}
                              historyHref={`/activities/${activity.id}/history`}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {pageItems.length > 0 && (
                      <ListTotalRow
                        columnWidths={columns.map(([, width]) => width)}
                        amounts={{ 2: totalAmount }}
                        actionColumnWidth={TABLE_ACTION_COL_WIDTH}
                      />
                    )}
                  </div>
                </Box>

                <ListPagination
                  label={paginationLabel}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('activities.empty')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((activity) => {
                const status = activityLockState(activity.weekReportId);
                return (
                  <Stack
                    key={activity.id}
                    gap={10}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text fz={14.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {formatWeekLabel(activity.weekNumber, activity.reportYear)}
                        </Text>
                        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                          {activity.clientOrgName ?? '—'}
                          {' · '}
                          <Text
                            span
                            inherit
                            role={activity.contractId ? 'button' : undefined}
                            tabIndex={activity.contractId ? 0 : undefined}
                            style={{ cursor: activity.contractId ? 'pointer' : undefined }}
                            onClick={() =>
                              activity.contractId && navigate(`/contracts/${activity.contractId}`)
                            }
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                activity.contractId
                              )
                                navigate(`/contracts/${activity.contractId}`);
                            }}
                          >
                            {contractLabel(activity.contractId)}
                          </Text>
                          {' · '}
                          <Text
                            span
                            inherit
                            role={activity.workPackageId ? 'button' : undefined}
                            tabIndex={activity.workPackageId ? 0 : undefined}
                            style={{ cursor: activity.workPackageId ? 'pointer' : undefined }}
                            onClick={() =>
                              activity.workPackageId &&
                              navigate(`/work-packages/${activity.workPackageId}`)
                            }
                            onKeyDown={(event) => {
                              if (
                                (event.key === 'Enter' || event.key === ' ') &&
                                activity.workPackageId
                              )
                                navigate(`/work-packages/${activity.workPackageId}`);
                            }}
                          >
                            {workPackageLabel(activity.workPackageId)}
                          </Text>
                          {activity.objectName && (
                            <>
                              {' · '}
                              <Text
                                span
                                inherit
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  navigate(`/templates/object-registry/${activity.objectId}`)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ')
                                    navigate(`/templates/object-registry/${activity.objectId}`);
                                }}
                              >
                                {activity.objectName}
                              </Text>
                            </>
                          )}
                        </Text>
                        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                          {t('activities.colAmount')}: {formatAmount(activity.totalAmount)}
                          {' · '}
                          {t('activities.colHours')}: {activity.totalHours ?? 0}
                        </Text>
                      </Stack>
                      <RowActionsMenu
                        detailsHref={`/activities/${activity.id}`}
                        historyHref={`/activities/${activity.id}/history`}
                      />
                    </Group>
                    <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} />
                  </Stack>
                );
              })}

              <ListTotalCard amount={totalAmount} />

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
